import { useGSAP } from '@gsap/react';
import gsap from 'gsap';

import * as Sentry from '@sentry/react';

import { graphql, useStaticQuery } from 'gatsby';
import React, { useEffect, useRef, useState } from 'react';
import { MdArrowForward, MdOutlineArrowOutward } from 'react-icons/md';
import { ALL_LANGS, EXPERIENCES_PREFIX_URL } from '../../../constants';
import { ButtonLinkType } from '../../../graphql-fragments/ButtonLink';
import { BREAKPOINTS } from '../../../styles/breakpoints';
import { Experience } from '../../../templates/ExperiencePage';
import { APIExperience, CMSPlusAPIExperience } from '../../../templates/ExperiencesPage';
import i18n from '../../../translation/i18n';
import { mapBy, uniqBy } from '../../../utils/nodash';
import { localize } from '../../../utils/sanity';
import { urlJoin } from '../../../utils/utils';
import bookingKitClient from '../../../vendorApis/bookingkit';
import { CommonModuleProps } from '../../ModulesContent';
import AnimatedTitle from '../AnimatedTitle';
import ButtonLink from '../ButtonLink';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import PriceTag from '../PriceTag';
import SmartLink from '../SmartLink';
import * as styles from './ExperienceCardModule.module.scss';

export const ExperienceCardModuleFragment = graphql`
  fragment ExperienceCardModule on SanityExperienceCardModule {
    title {
      ...LocaleStringFragment
    }
    text {
      ...LocaleTextFragment
    }
    button {
      ...ButtonLink
    }
    experiencesToUse
    chosenExperiences: experiences {
      title {
        ...LocaleStringFragment
      }
      slug {
        current
      }
    }
  }
`;

export type ExperienceCardModuleProps = {
  title: string;
  text?: string;
  button?: ButtonLinkType;
  experiencesToUse?: 'all' | 'chooseManually';
  chosenExperiences: Array<Experience>;
  className?: string;
};

interface LocalizedQueryData {
  allSanityExperience: {
    nodes: Array<Experience>;
  };
}

function ExperienceCardModule(
  props: ExperienceCardModuleProps & CommonModuleProps,
): React.ReactElement {
  const staticData = useStaticQuery<LocalizedQueryData>(graphql`
    {
      allSanityExperience(sort: { orderRank: ASC }) {
        nodes {
          ...Experience
        }
      }
    }
  `);

  const containerRef = useRef<HTMLElement | null>(null);

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: '.' + styles.cardsContainer,
          start: 'center bottom',
        } satisfies ScrollTrigger.Vars,
      });

      tl.from(`.${styles.cardContainer}`, {
        y: 100,
        autoAlpha: 0,
        duration: 0.8,
        stagger: 0.2,
      });
    },
    { scope: containerRef },
  );

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: LocalizedQueryData = localize(staticData, langs);

  const { title, text, chosenExperiences, experiencesToUse, button, className, moduleId } = props;

  function mergeCMSExperienceWithAPI(
    cmsExperience: Experience,
    apiExperience: APIExperience,
  ): CMSPlusAPIExperience {
    return {
      ...cmsExperience,
      price: apiExperience.prices.find(price => price.title === 'Standard')!.value,
      dates: apiExperience.dates,
    };
  }

  const experiencesToShow =
    experiencesToUse === 'chooseManually'
      ? chosenExperiences
      : localizedData.allSanityExperience.nodes;

  const [experiencesFromApi, setExperiencesFromApi] = useState<Array<CMSPlusAPIExperience> | null>(
    null,
  );

  useEffect(() => {
    void (async () => {
      try {
        const apiExperiences = await Promise.all(
          experiencesToShow
            .map(experience => experience.bookingKitId)
            .map(id => bookingKitClient.getEventDetails(id, { withDates: true })),
        );
        const apiExperiencesById = mapBy(uniqBy(apiExperiences, 'id'), 'id');
        setExperiencesFromApi(
          experiencesToShow.map(cmsExperience =>
            mergeCMSExperienceWithAPI(
              cmsExperience,
              apiExperiencesById[cmsExperience.bookingKitId]!,
            ),
          ),
        );
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
      }
    })();
  }, []);

  function renderExperienceCard(experience: Experience | CMSPlusAPIExperience) {
    return (
      <SmartLink
        className={styles.cardContainer}
        to={urlJoin(EXPERIENCES_PREFIX_URL, experience.slug.current)}
      >
        {'price' in experience && <PriceTag price={experience.price} className={styles.priceTag} />}
        <div className={styles.backgroundImageContainer}>
          <Image
            image={experience.image}
            className={styles.image}
            dimensions={[
              [900, 300],
              [BREAKPOINTS.tabletVertical, 700, 700],
            ]}
            cover
          />
        </div>
        <div className={styles.overlay}></div>
        <h3 className={styles.experienceTitle}>
          <MdOutlineArrowOutward className={styles.titleIcon} /> {experience.title}
          <MdArrowForward className={styles.titleGoIcon} />
        </h3>
      </SmartLink>
    );
  }

  return (
    <ModuleLayout
      id={moduleId}
      ref={containerRef}
      className={className}
      currentModuleBgColor={props.theme}
      contentClassName={styles.contentContainer}
    >
      <div className={styles.textContainer}>
        <div className={styles.titleAndTextContainer}>
          <AnimatedTitle title={title} trigger={styles.contentContainer} className={styles.title} />
          <p className={styles.text}>{text}</p>
        </div>
        {button && <ButtonLink button={button} />}
      </div>
      <div className={styles.cardsContainer}>
        {(experiencesFromApi ? experiencesFromApi : experiencesToShow).map((experience, i) => (
          <React.Fragment key={i}>{renderExperienceCard(experience)}</React.Fragment>
        ))}
      </div>
    </ModuleLayout>
  );
}

export default ExperienceCardModule;
